import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { BottomPanelKey, EventLeaveReason, UserActionType } from 'types';
import { getCallbackTriggersMap, getEventURL } from 'utils';
import {
  authSelector,
  callbackTriggersSelector,
  currentPlanSelector,
  hasAvailableMinutesSelector,
  hasChannelSelector,
  isOnPaidPlanSelector,
  profileChannelNameSelector,
  profileUserSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { tracker } from 'services';
import { useBottomSheet, useRouter, useStore } from 'hooks';
import { BottomPanels, Events, FreeTrialPanel, Layout, SchedulePanel, SetupPanel } from 'containers';
import { layoutContext } from 'containers/Layout/Layout';
import { CalendarIcon, CameraIcon } from 'components/Icon';

import styles from './Welcome.module.scss';

export type WelcomeProps = {
  channelName?: string;
  onBecomeMember?(channelId: string, channelName: string): void;
};

const Welcome = ({ channelName }: WelcomeProps): JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter<{ eventCode?: string; channelName?: string; reason?: EventLeaveReason }>();
  const layout = useContext(layoutContext);
  const {
    auth,
    user,
    profileChannelName,
    hasAvailableMinutes,
    hasOwnChannel,
    isOnPaidPlan,
    currentPlan,
    callbackTriggers
  } = useStore(state => ({
    auth: authSelector(state),
    user: profileUserSelector(state),
    profileChannelName: profileChannelNameSelector(state),
    hasAvailableMinutes: hasAvailableMinutesSelector(state),
    hasOwnChannel: hasChannelSelector(state),
    isOnPaidPlan: isOnPaidPlanSelector(state),
    currentPlan: currentPlanSelector(state),
    callbackTriggers: callbackTriggersSelector(state)
  }));

  const bottomSheet = useBottomSheet<
    BottomPanelKey.FREE_TRIAL | BottomPanelKey.SETUP | BottomPanelKey.SCHEDULE,
    { channelId?: string }
  >();

  const isOnFreeTrial = user.channelName && user.onFreeTrial && user.availableMinutes > 0;

  const isProfileActive = isOnFreeTrial || isOnPaidPlan;

  useEffect(() => {
    if (auth.isAuth && callbackTriggers.length) {
      const callbackTriggersMap = getCallbackTriggersMap(callbackTriggers);

      if (callbackTriggersMap[UserActionType.OPEN_FREE_TRIAL_PANEL]) {
        openFreeTrialPanel();

        dispatch(callbackTriggersActions.remove(UserActionType.OPEN_FREE_TRIAL_PANEL));
      }

      if (callbackTriggersMap[UserActionType.OPEN_SETUP_PANEL]) {
        openSetupPanel();

        dispatch(callbackTriggersActions.remove(UserActionType.OPEN_SETUP_PANEL));
      }

      if (callbackTriggersMap[UserActionType.OPEN_SCHEDULE_PANEL]) {
        openSchedulePanel();

        dispatch(callbackTriggersActions.remove(UserActionType.OPEN_SCHEDULE_PANEL));
      }
    }
  }, [auth.isAuth]);

  const goToEvent = (_channelName: string, _eventCode: string, { audioMuted, videoMuted }) => {
    router.push(getEventURL(_channelName, _eventCode), undefined, {
      from: 'setup_panel',
      defaultAudioMuted: audioMuted,
      defaultVideoMuted: videoMuted
    });
  };

  const openFreeTrialPanel = () => {
    if (!auth.isAuth) {
      dispatch(callbackTriggersActions.set([{ type: UserActionType.OPEN_FREE_TRIAL_PANEL }]));
      router.push('/login');
    } else {
      bottomSheet.open(BottomPanelKey.FREE_TRIAL);
    }
  };

  const openSetupPanel = () => {
    if (!auth.isAuth) {
      dispatch(callbackTriggersActions.set([{ type: UserActionType.OPEN_SETUP_PANEL }]));
      router.push('/login');
    } else if (!isProfileActive || !hasAvailableMinutes) {
      openFreeTrialPanel();
    } else {
      bottomSheet.open(BottomPanelKey.SETUP);
    }
  };

  const openSchedulePanel = () => {
    if (!auth.isAuth) {
      dispatch(callbackTriggersActions.set([{ type: UserActionType.OPEN_SCHEDULE_PANEL }]));
      router.push('/login');
    } else if (!isProfileActive || !hasAvailableMinutes) {
      openFreeTrialPanel();
    } else {
      bottomSheet.open(BottomPanelKey.SCHEDULE);
    }
  };

  const openPricingPage = () => router.replace('/pricing');

  const onOpenCurrentPlan = () => {
    layout.openProfile();
  };

  const handleOnWatch = (_channelName: string, _eventCode: string) => {
    tracker.watchEvent(_eventCode);
    router.push(getEventURL(_channelName, _eventCode));
  };

  const handleOnScheduleSubmitted = (_channelName: string, eventCode: string) => {
    router.push(getEventURL(_channelName, eventCode));
  };

  const renderTitle = () => {
    if (!profileChannelName) {
      return (
        <button type="button" className={styles.title} onClick={openFreeTrialPanel}>
          Start Free Trial
        </button>
      );
    }

    if (!hasAvailableMinutes) {
      return (
        <button type="button" className={styles.title} onClick={openPricingPage}>
          Upgrade
        </button>
      );
    }

    return (
      <button type="button" className={styles.title} onClick={onOpenCurrentPlan}>
        {currentPlan?.name}
      </button>
    );
  };

  return (
    <>
      <Layout.Header className={styles.header} title={renderTitle()} />
      <Layout.Content>
        <div className="container">
          <Events channelName={channelName} onWatch={handleOnWatch} />
        </div>
      </Layout.Content>

      <Layout.Footer className={styles.footer}>
        <button type="button" className={styles.schedule} onClick={openSchedulePanel}>
          <CalendarIcon />
          <span>Schedule event</span>
        </button>

        <button type="button" className={styles.start} onClick={openSetupPanel}>
          <CameraIcon />
          <span>Start event</span>
        </button>
      </Layout.Footer>

      <BottomPanels
        panels={[
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.SETUP,
            show: bottomSheet.state.contentKey === BottomPanelKey.SETUP,
            title: 'Start event',
            component: <SetupPanel type="new" onSubmitted={goToEvent} />
          },
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.SCHEDULE,
            show: bottomSheet.state.contentKey === BottomPanelKey.SCHEDULE,
            title: 'Schedule event',
            component: <SchedulePanel onSubmitted={handleOnScheduleSubmitted} />
          }
        ]}
        onClose={bottomSheet.close}
      />

      <FreeTrialPanel
        show={bottomSheet.state.contentKey === BottomPanelKey.FREE_TRIAL}
        onSchedule={openSchedulePanel}
        onStart={openSetupPanel}
        onClose={bottomSheet.close}
      />
    </>
  );
};

export default Welcome;
